// frontend/src/pages/canvasser/ActionRouteScreen.tsx
import { useMemo, useRef, useState } from 'react'
import { Alert, Box, Button, Flex, HStack, Spinner, Text, VStack } from '@chakra-ui/react'
import { Navigate, useSearchParams } from 'react-router-dom'
import { toaster } from '@/components/ui/toaster'
import { useMyTurfs } from '../../hooks/useCanvasser'
import { useGrantCampaign } from '../../hooks/useGrantCampaign'
import { useGeolocation } from '../../hooks/useGeolocation'
import { useWalkRoute } from '../../hooks/useWalkRoute'
import { useRecordRouteStop } from '../../hooks/useRecordRouteStop'
import { doorToast } from '../../hooks/useRecordDoor'
import { useToday } from '../../hooks/useToday'
import { openableCards } from '../../lib/todayAssignments'
import { useSync } from '../../providers/SyncProvider'
import { isCacheExpiredError } from '../../lib/offlineStore'
import {
  actionStopState, buildWalkStops, nextStopAfter, orderStops, sequenceOf, stateRuleFor, walkProgress,
} from '../../lib/walkStops'
import { parseLine } from '../../lib/walkGeo'
import { readLeftStop, readWalkPrefs, rememberLeftStop, writeWalkPrefs } from '../../lib/walkPrefs'
import { CacheExpiredNotice } from '../../components/canvasser/CacheExpiredNotice'
import { CanvasserMapView } from '../../components/canvasser/CanvasserMapView'
import { TodayBackLink } from '../../components/canvasser/TodayBackLink'
import { ActionStopCard } from '../../components/canvasser/route/ActionStopCard'
import { CouldntReasonSheet } from '../../components/canvasser/route/CouldntReasonSheet'
import type { RouteOutcome } from '../../types/canvasser'

const NO_KEYS: ReadonlySet<string> = new Set()
const actionRule = stateRuleFor('ActionRoute')

type TravelMode = 'walking' | 'driving'

/**
 * [CF-1 P3 §7.3] An action route (sign drop-off etc.): header, map, and ONE current stop with
 * "Dropped off" / "Couldn't". Outcomes go through `useRecordRouteStop` (route-outcomes), never a
 * visit, script or conversation. `lib/walkStops.ts` (ActionRoute rule) is the only status source.
 */
export function ActionRouteScreen({ turfId, showTodayLink }: { turfId: string; showTodayLink: boolean }) {
  const campaign = useGrantCampaign()
  const { isOnline } = useSync()
  const { data: turfs, isLoading, error } = useMyTurfs(campaign?.id)
  const turf = turfs?.find(t => t.turfId === turfId)
  const walkType = turf?.walkType ?? 'Walk'

  const [modeOverride, setModeOverride] = useState<{ turfId: string; mode: TravelMode } | null>(null)
  const travelMode: TravelMode = modeOverride?.turfId === turfId
    ? modeOverride.mode
    : walkType === 'Drive' ? 'driving' : 'walking'

  const stops = useMemo(
    () => (turf ? buildWalkStops(turf.contacts, turf.addressMode, NO_KEYS, actionRule) : []),
    [turf])
  const openStops = useMemo(() => stops.filter(s => s.state !== 'done'), [stops])
  const { position, recenter } = useGeolocation()
  const route = useWalkRoute({ stops: openStops, turfId, mode: travelMode, currentPosition: position, enabled: true,
    orderIsManual: turf?.orderIsManual ?? false })
  const routable = openStops.filter(s => s.lat != null && s.lng != null).length >= 2
  const routeData = routable ? route.routeData : null
  const routeLine = useMemo(() => parseLine(routeData?.routeGeoJson), [routeData?.routeGeoJson])
  const nextSegmentLine = useMemo(() => parseLine(routeData?.nextSegmentGeoJson), [routeData?.nextSegmentGeoJson])

  const ordered = useMemo(() => orderStops(stops, route.order), [stops, route.order])
  const sequence = useMemo(() => sequenceOf(ordered), [ordered])
  const progress = useMemo(() => walkProgress(stops), [stops])

  // The last stop an outcome was recorded at: survives a reload (sessionStorage), so the route resumes.
  const [lastRecorded, setLastRecorded] = useState<{ turfId: string; key: string | null }>(
    () => ({ turfId, key: readLeftStop(turfId)?.key ?? null }))
  const lastRecordedKey = lastRecorded.turfId === turfId ? lastRecorded.key : readLeftStop(turfId)?.key ?? null
  const current = useMemo(() => nextStopAfter(ordered, lastRecordedKey), [ordered, lastRecordedKey])

  const [replanTipSeen, setReplanTipSeen] = useState(() => readWalkPrefs().replanTipSeen)
  const [sheetOpen, setSheetOpen] = useState(false)
  // [R6] The stop whose outcome the server permanently rejected: the driver may skip past it locally.
  const [rejectedKey, setRejectedKey] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const savingRef = useRef(false)
  const recordStop = useRecordRouteStop().mutate

  const record = (outcome: RouteOutcome, reason: string | null) => {
    if (!current || savingRef.current) return
    const stop = current
    const contactIds = stop.residents.filter(r => actionStopState(r) !== 'done').map(r => r.contactId)
    savingRef.current = true
    setSaving(true)
    recordStop({ turfId, contactIds, outcome, reason }, {
      onSuccess: result => {
        toaster.create(doorToast(result, 'drop-off'))
        // Rejected: the stop stays current; the toast carries the server's message, and "Skip this
        // stop" is offered (R6). The rejected outcome stays in the sync banner.
        if (result.rejected.length > 0) { setRejectedKey(stop.key); return }
        setRejectedKey(null)
        rememberLeftStop(turfId, { key: stop.key, state: stop.state, recorded: true })
        setLastRecorded({ turfId, key: stop.key })
      },
      onError: () => {
        toaster.create({ type: 'error', title: "Couldn't save this stop. Try again." })
      },
      onSettled: () => {
        savingRef.current = false
        setSaving(false)
      },
    })
  }

  // [R6] Local advance only (no server write): the next stop after this one, like a recorded stop.
  const skip = () => {
    if (!current) return
    rememberLeftStop(turfId, { key: current.key, state: current.state })
    setLastRecorded({ turfId, key: current.key })
    setRejectedKey(null)
  }

  if (isLoading) return <VStack py={12}><Spinner size="lg" color="accent" /></VStack>
  if (error) {
    if (isCacheExpiredError(error)) return <CacheExpiredNotice />
    return (
      <Box p={4}>
        <Alert.Root status="error" borderRadius="md"><Alert.Indicator />We couldn't load your route. Try again in a moment.</Alert.Root>
      </Box>
    )
  }
  if (!turf) return null

  const title = turf.kindLabel ?? 'Drop-off'

  if (!current) {
    return (
      <VStack gap={3} py={12} px={6} pb="calc(48px + env(safe-area-inset-bottom, 0px))">
        <Text fontWeight="bold" fontSize="lg">{title}</Text>
        <Text textAlign="center">{`Route finished. ${progress.done} of ${progress.total} stops.`}</Text>
        {showTodayLink && <TodayBackLink />}
      </VStack>
    )
  }

  return (
    <Flex direction="column" h="100%" minH={0}>
      <HStack px={showTodayLink ? 2 : 4} py={2} gap={2} borderBottomWidth="1px" borderColor="border" bg="surface" flexShrink={0}>
        {showTodayLink && <TodayBackLink />}
        <Text fontWeight="bold" flex={1} minW={0} lineClamp={1}>{title}</Text>
        <Text fontSize="sm" color="fg.muted" flexShrink={0} pr={showTodayLink ? 2 : 0}>
          {`Stop ${sequence.get(current.key) ?? ''} of ${progress.total}`}
        </Text>
      </HStack>

      <Box flex={1} minH={0} position="relative" overflow="hidden">
        {isOnline ? (
          <CanvasserMapView stops={ordered} visibleKeys={null} sequence={sequence} selectedKey={current.key}
            onSelectStop={() => {}} activeFilterLabel={null}
            routeGeoJson={routeLine} nextSegmentGeoJson={nextSegmentLine} gpsPosition={position}
            nextDistance={routeData?.nextSegmentDistanceMeters ?? null}
            nextDuration={routeData?.nextSegmentDurationSeconds ?? null}
            travelMode={travelMode} onTravelModeChange={mode => setModeOverride({ turfId, mode })}
            orderLocked={route.orderLocked} onReoptimize={route.reoptimize} isRouteOptimized={route.isRouteOptimized}
            optimizeBlockedReason={route.optimizeBlockedReason ?? null}
            routingUnavailable={route.routingUnavailable} onRecenter={recenter} isRefreshing={route.isFetching}
            showKindIcons={false} routeState={route.routeState} replanTipSeen={replanTipSeen}
            onDismissReplanTip={() => { setReplanTipSeen(true); writeWalkPrefs({ ...readWalkPrefs(), replanTipSeen: true }) }} />
        ) : (
          <VStack h="100%" justify="center" px={6}>
            <Text textAlign="center" color="fg.muted">The map needs a connection. Your stops work offline.</Text>
          </VStack>
        )}
      </Box>

      {rejectedKey === current.key && (
        <HStack px={4} py={2} gap={2} bg="surface" borderTopWidth="1px" borderColor="border" flexShrink={0}>
          <Text fontSize="sm" color="fg.muted" flex={1} minW={0}>This stop couldn't be saved.</Text>
          <Button minH="48px" variant="outline" borderColor="border" flexShrink={0} disabled={saving} onClick={skip}>
            Skip this stop
          </Button>
        </HStack>
      )}

      <ActionStopCard stop={current} travelMode={travelMode} saving={saving}
        onDone={() => record('Done', null)} onCouldnt={() => setSheetOpen(true)} />

      <CouldntReasonSheet open={sheetOpen} onClose={() => setSheetOpen(false)}
        onSubmit={reason => { setSheetOpen(false); record('Couldnt', reason) }} />
    </Flex>
  )
}

/**
 * `/canvasser/action-route?turfId=` — the deep link. The home normally renders the screen inline.
 * An unknown or non-action turf never opens a blank screen: it goes back to the home.
 */
export function ActionRouteRoute() {
  const [params] = useSearchParams()
  const turfId = params.get('turfId')
  const campaign = useGrantCampaign()
  const turfs = useMyTurfs(campaign?.id)
  const today = useToday(campaign?.id)
  if (turfs.isLoading) return <VStack py={12}><Spinner size="lg" color="accent" /></VStack>
  if (turfs.error && isCacheExpiredError(turfs.error)) return <CacheExpiredNotice />
  const turf = turfId ? turfs.data?.find(t => t.turfId === turfId) : undefined
  if (!turf || turf.assignmentKind !== 'ActionRoute') return <Navigate to="/canvasser" replace />
  // Same count as the home (m4): only cards the home can open.
  return <ActionRouteScreen key={turf.turfId} turfId={turf.turfId} showTodayLink={openableCards(today.cards).length > 1} />
}
